import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import type {
  BackendUpdateErrorResponse,
  DatastoreCollection,
} from '@freelancer/datastore';
import { Datastore } from '@freelancer/datastore';
import type {
  Contest,
  Project,
  ProjectBookmarksCollection,
} from '@freelancer/datastore/collections';
import {
  ProjectBookmark,
  ProjectBookmarkType,
} from '@freelancer/datastore/collections';
import {
  flBookmarkSolidV2,
  flBookmarkV2,
  IconColor,
  IconSize,
  provideFlIcons,
} from '@freelancer/ui/icon';
import { TagType } from '@freelancer/ui/tag';
import type { Observable } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-project-bookmark-button',
  template: `
    @if (isTag) {
      <fl-tag
        flTrackingLabel="projectBookmarkButton"
        [truncate]="false"
        [type]="TagType.CLICKABLE"
        [iconConfig]="{
          name: bookmark.subscribed ? 'flBookmarkSolidV2' : 'flBookmarkV2',
          size: IconSize.SMALL,
        }"
        (click)="handleClick($event)"
      >
        <span
          class="text-body-16 font-bold"
          i18n="Bookmark label"
        >
          Bookmark
        </span>
      </fl-tag>
    } @else {
      @if (!bookmark.subscribed) {
        <fl-icon
          flTrackingLabel="projectBookmarkButton"
          i18n-label="Bookmark label"
          label="Bookmark"
          [clickable]="true"
          [color]="color"
          [name]="'ui-bookmark-v2'"
          [useIconFont]="true"
          (click)="handleClick($event)"
        ></fl-icon>
      } @else {
        <fl-icon
          flTrackingLabel="projectBookmarkButton"
          i18n-label="Remove bookmark label"
          label="Remove Bookmark"
          [clickable]="true"
          [color]="colorSubscribed"
          [name]="'ui-bookmark-v2'"
          [useIconFont]="true"
          [fillIconFont]="true"
          (click)="handleClick($event)"
        ></fl-icon>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideFlIcons({ flBookmarkSolidV2, flBookmarkV2 })],
})
export class ProjectBookmarkButtonComponent {
  IconSize = IconSize;
  TagType = TagType;

  private datastore = inject(Datastore);

  bookmark$: Observable<ProjectBookmark>;
  bookmarksCollection: DatastoreCollection<ProjectBookmarksCollection>;

  @Input() color = IconColor.FOREGROUND;
  @Input() colorSubscribed = IconColor.SECONDARY;
  @Input() project: Project | Contest;
  @Input() type: ProjectBookmarkType;
  @Input() bookmark: ProjectBookmark;
  @Input() isTag? = false;

  @Output() error = new EventEmitter<
    BackendUpdateErrorResponse<ProjectBookmarksCollection>
  >();
  @Output() success = new EventEmitter<void>();

  handleClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    this.datastore
      .document<ProjectBookmarksCollection>(
        'projectBookmarks',
        this.bookmark.id,
      )
      .update({ subscribed: !this.bookmark.subscribed })
      .then(response => {
        if (response.status === 'success') {
          if (!this.bookmark.subscribed) {
            this.success.emit();
          }
        } else {
          this.error.emit(response);
        }
      });
  }
}
